import { type ValueOf } from '@kc-console/core';

import type { ReactNode } from 'react';
import type { ButtonProps } from '../Button';
import type { ButtonVariant } from '../Button/constants';
import type { TooltipProps } from '../Tooltip';

const ModalFooterButtonEventSet = [
  'cancel',
  'close',
  'submit',
  'delete',
  'confirm',
  'common',
] as const;
type ModalFooterButtonEvent = ValueOf<typeof ModalFooterButtonEventSet>;

/**
 * 제출로 취급하는 이벤트 타입입니다.
 * @description
 * - 이 타입의 버튼은 렌더 커밋 전 중복 클릭이 throttle로 차단됩니다.
 * - `common`은 기본 라벨이 '확인', variant가 `primary`라 제출 버튼으로 쓰일 수 있어 포함합니다.
 * - 취소·닫기 계열은 연속 클릭을 막을 이유가 없어 제외합니다.
 */
const SUBMIT_EVENT_TYPES: ReadonlyArray<ModalFooterButtonEvent> = [
  'submit',
  'delete',
  'confirm',
  'common',
];

type ModalFooterButtonOptionMapByEvent<Option> = Partial<Record<ModalFooterButtonEvent, Option>>;

type ModalFooterButtonVariantMapByEvent = ModalFooterButtonOptionMapByEvent<ButtonVariant>;
type ModalFooterButtonLabelMapByEvent = ModalFooterButtonOptionMapByEvent<any>;

type ModalFooterContextParams = {
  /**
   * ModalFooter 영역에 배치할 버튼의 이벤트 별 라벨을 커스텀합니다.
   * @default {cancel:'취소', close:'닫기', submit: '적용', delete: '삭제', confirm: '확인', common: '확인'}
   */
  labelMap: ModalFooterButtonLabelMapByEvent;
  /**
   * ModalFooter 영역의 로딩 상태를 설정합니다.
   */
  loading: boolean;
  /**
   * ModalFooter 영역에 배치할 버튼의 이벤트 별 유형을 커스텀합니다.
   * @default {cancel: 'tertiary',  close: 'tertiary',  submit: 'primary',  delete: 'danger',  confirm: 'primary',  common: 'primary'}
   */
  variantMap: ModalFooterButtonVariantMapByEvent;
};

type ModalFooterButtonProps = {
  /**
   * E2E 테스트에서 버튼을 식별할 때 사용합니다.
   * 지정하지 않으면 eventType을 기반으로 취소 계열은 `footer-cancel`,
   * 확정 계열은 `footer-confirm`으로 설정됩니다.
   */
  'data-testid'?: string;
  /**
   * button disabled = `true` 일 때 표시할 툴팁 메시지 및 툴팁의 옵션을 설정합니다.
   */
  disabledTooltip?: Omit<TooltipProps, 'children'>;
  /**
   * 버튼의 이벤트 타입을 설정합니다.
   * eventType 설정에 따라 ModalFooter 컴포넌트에 설정한 colorMap, labelMap 에 매칭되는 색상과 라벨을 표시합니다.
   * eventType을 설정하지 않을 경우, color는 ModalFooter.Button의 props 중 color / label은 ModalFooter.Button의 children을 사용합니다.
   */
  eventType?: ModalFooterButtonEvent;
} & ButtonProps;

type ModalFooterProps = Partial<ModalFooterContextParams> & {
  buttons?: Array<ReactNode>;
  /**
   * ModalFooter 영역의 로딩 상태를 설정합니다.
   *
   * ModalFooterButton 컴포넌트에 `disabled={true}`가 설정됩니다. 단 ModalFooterButton 컴포넌트에 `disabled를` 직접 지정한 경우에는 설정되지 않습니다.
   */
  loading?: boolean;
};

export type {
  ModalFooterProps,
  ModalFooterButtonProps,
  ModalFooterContextParams,
  ModalFooterButtonLabelMapByEvent,
  ModalFooterButtonVariantMapByEvent,
  ModalFooterButtonEvent,
};

export { ModalFooterButtonEventSet, SUBMIT_EVENT_TYPES };
